import { type UseDropZoneOptions } from '@vueuse/core';
import { type MaybeRef, toValue } from 'vue';
import { useUploadFile, type WebAppFileType } from '@composable/useUploadFile';

interface FileDescriptor {
    id: number;
    file: File;
    size: string;
}

const useDropzone = (
    options?: UseDropZoneOptions & {
        dataTypes?: MaybeRef<string[]>;
        webAppFileType?: MaybeRef<WebAppFileType>;
        webAppFileExtensions?: MaybeRef<string[]>;
        maxNumberOfFiles?: number;
        maxFileSize?: number;
        customValidator?: (file: File) => boolean;
        onChange?: (files: FileDescriptor[], file: File | null) => void;
    }
) => {
    const dropZoneRef = ref<HTMLDivElement>();
    const files = ref<FileDescriptor[]>([]);
    const upload = useUploadFile(
        {
            fileType: toValue(options?.webAppFileType),
            extensions: toValue(options?.webAppFileExtensions),
            maxFiles: options?.maxNumberOfFiles,
        },
        file => {
            files.value = file
                .filter(file => validator(file))
                .map(file => ({
                    id: randomId(),
                    file,
                    size: `${Math.round(file.size / 1024).toFixed(2)} KB`,
                }));
        }
    );

    const randomId = () => Date.now() + Math.floor(Math.random() * 100);
    const validator = (file: File) => {
        const numberOfFileCondition = options?.maxNumberOfFiles
            ? files.value.length < options.maxNumberOfFiles
            : true;

        const maxFileSizeCondition =
            options?.maxFileSize && file
                ? file.size <= options.maxFileSize
                : true;

        const customValidatorCondition = options?.customValidator
            ? options.customValidator(file)
            : true;

        const dataTypesCondition = options?.dataTypes
            ? toValue(options?.dataTypes)
                  ?.map(type =>
                      type.includes('/*') ? type.replace('/*', '') : type
                  )
                  .some(type => file.type.includes(type))
            : true;

        return (
            numberOfFileCondition &&
            maxFileSizeCondition &&
            customValidatorCondition &&
            dataTypesCondition
        );
    };

    const setFile = (file: File) => {
        if (validator(file)) {
            files.value = [
                ...files.value,
                {
                    id: randomId(),
                    file,
                    size: `${Math.round(file.size / 1024).toFixed(2)} KB`,
                },
            ];
        }
    };

    function onDrop(files: File[] | null, event: DragEvent) {
        event.preventDefault();
        options?.onDrop?.(files, event);
        files && setFile(files[0]);
        if (options?.onDrop) {
            options.onDrop(files, event);
        }
    }

    const { open, onChange } = useFileDialog({
        accept: toValue(options?.dataTypes)?.join(','),
    });

    const { isOverDropZone } = useDropZone(dropZoneRef, {
        ...options,
        // dataTypes will lead to dropped file open in current browser tab if file type is not matched
        // for some reason not respond correctly to /* (all type of image - image/* e.g.)
        dataTypes: undefined,
        onDrop,
    });

    const removeFile = (fileId: number) => {
        files.value = files.value.filter(({ id }) => id !== fileId);
        options?.onChange?.(files.value, null);
    };

    onChange(input => {
        if (input) {
            setFile(input[0]);
        }
        options?.onChange?.(files.value, input?.[0] ?? null);
    });

    const reachedMax = computed(() =>
        options?.maxNumberOfFiles
            ? files.value.length >= options.maxNumberOfFiles
            : false
    );

    return {
        files,
        setFile,
        isOverDropZone,
        removeFile,
        dropZoneRef,
        openFileSelect: upload || open,
        reachedMax,
    };
};

export default useDropzone;
